import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '@rti/shared';
import { Eye, EyeOff } from 'lucide-react';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'checking' | 'needs_password' | 'verifying' | 'success' | 'error'>('checking');
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(10);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [userInfo, setUserInfo] = useState<{ email: string; first_name: string } | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const checkAttempted = useRef(false);
  const showSupportLink = ((import.meta as any).env?.VITE_SHOW_SUPPORT_LINK as string | undefined) === 'true';

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setError('Invalid verification link. Please check your email and try again.');
      return;
    }

    if (checkAttempted.current) {
      return;
    }
    checkAttempted.current = true;

    const checkToken = async () => {
      try {
        const response = await apiClient.checkVerificationToken(token);

        if (response.valid) {
          if (response.needs_password) {
            setNeedsPassword(true);
            setUserInfo({ email: response.email, first_name: response.first_name });
            setStatus('needs_password');
          } else {
            await handleVerifyEmail(token);
          }
        }
      } catch (err: any) {
        console.error('Token check error:', err);
        setStatus('error');
        const errorMsg = err?.response?.data?.detail || err?.message || 'Invalid or expired verification link';
        setError(errorMsg);
      }
    };

    checkToken();
  }, [searchParams]);

  const handleVerifyEmail = async (token: string, pwd?: string) => {
    setStatus('verifying');
    setPasswordError('');
    try {
      const response = await apiClient.verifyEmail(token, pwd);

      if (response && response.success) {
        if (response.token) {
          localStorage.setItem('access_token', response.token);
        }
        if (response.user) {
          localStorage.setItem('user', JSON.stringify(response.user));
        }
        localStorage.setItem('last_activity', Date.now().toString());

        setStatus('success');

        const interval = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              window.location.href = '/dashboard';
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setStatus('error');
        setError(response?.message || 'Email verification failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Email verification error:', err);
      setStatus('needs_password');
      const errorMsg = err?.response?.data?.detail || err?.message || 'Verification failed';
      setPasswordError(errorMsg);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (!password) {
      setPasswordError('Password is required');
      return;
    }

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    const token = searchParams.get('token');
    if (token) {
      await handleVerifyEmail(token, password);
    }
  };

  const handleResendEmail = async () => {
    setResendLoading(true);
    setResendMessage('');

    let email = '';
    if (userInfo?.email) {
      email = userInfo.email;
    } else {
      email = prompt('Please enter your email address to resend verification:') || '';
      if (!email) {
        setResendLoading(false);
        setResendMessage('Email address is required');
        return;
      }
    }

    try {
      await apiClient.resendVerification(email);
      setResendMessage('Verification email sent! Please check your inbox and spam folder.');
    } catch (err: any) {
      console.error('Resend verification error:', err);
      const errorMsg = err?.response?.data?.detail || err?.message || 'Failed to resend email';
      setResendMessage(errorMsg);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="root">
      <div className="auth-root flex w-full h-[100vh]">
        <div className="flex flex-col justify-center items-center h-full w-full max-w-2xl px-8 py-10 bg-rs-black text-rs-off-white mx-auto">
          <img src="/logos/Primary/White.svg" alt="Resin Smart Logo" className="w-md mb-8" />

          <div className="text-center max-w-lg w-full">
            {status === 'checking' && (
              <>
                <div className="mb-6">
                  <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-rs-green mx-auto"></div>
                </div>
                <h1 className="text-3xl font-light mb-4">Checking Your Link...</h1>
                <p className="text-lg text-rs-off-white/80">
                  Please wait while we validate your verification link.
                </p>
              </>
            )}

            {status === 'needs_password' && (
              <>
                <h1 className="text-3xl font-light mb-4">Welcome to ResinSmart!</h1>
                <p className="text-lg text-rs-off-white/80 mb-6">
                  Hi {userInfo?.first_name}, please create a password for your account.
                </p>

                <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4 text-left">
                  <div className="mb-5">
                    <label className="block text-sm font-medium mb-2">Create Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-rs-jet text-rs-off-white p-3 pr-12 rounded border border-rs-jet-light focus:border-rs-green focus:outline-none"
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-rs-off-white/70 hover:text-rs-off-white"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <PasswordStrengthMeter password={password} showLabel showFeedback className="mt-2" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Confirm Password</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full bg-rs-jet text-rs-off-white p-3 pr-12 rounded border border-rs-jet-light focus:border-rs-green focus:outline-none"
                        placeholder="Confirm your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-rs-off-white/70 hover:text-rs-off-white"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {passwordError && (
                    <div className="bg-rs-red/20 border border-rs-red text-rs-red px-4 py-3 rounded">
                      {passwordError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-rs-green hover:bg-rs-green/80 !text-rs-black font-semibold py-3 px-4 rounded transition-colors"
                  >
                    Set Password & Verify Email
                  </button>
                </form>

                <p className="text-sm text-rs-off-white/60 mt-6">
                  Password must be at least 8 characters long
                </p>
              </>
            )}

            {status === 'verifying' && (
              <>
                <div className="mb-6">
                  <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-rs-green mx-auto"></div>
                </div>
                <h1 className="text-3xl font-light mb-4">{needsPassword ? 'Setting Up Your Account...' : 'Verifying Your Email...'}</h1>
                <p className="text-lg text-rs-off-white/80">
                  Please wait while we {needsPassword ? 'set up your account' : 'verify your email address'}.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="mb-6">
                  <svg className="w-20 h-20 mx-auto text-rs-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>

                <h1 className="text-3xl font-light mb-4 text-rs-green">Email Verified Successfully!</h1>

                <p className="text-lg text-rs-off-white/80 mb-6">
                  Your email has been verified. Welcome to ResinSmart!
                </p>

                <div className="bg-rs-jet/30 border border-rs-jet rounded-lg p-6 mb-6">
                  <p className="text-rs-off-white/90">
                    Redirecting you to your dashboard in <span className="text-rs-green font-bold text-xl">{countdown}</span> seconds...
                  </p>
                </div>
                <div className="flex items-center justify-center">
                  <button
                    onClick={() => window.location.href = '/dashboard'}
                    className="green !text-lg !px-8 !py-3"
                  >
                    Go to Dashboard Now
                  </button>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="mb-6">
                  <svg className="w-20 h-20 mx-auto text-rs-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>

                <h1 className="text-3xl font-light mb-4 text-rs-red">Verification Failed</h1>

                <p className="text-lg text-rs-off-white/80 mb-6">
                  {error}
                </p>

                <div className="bg-rs-jet/30 border border-rs-jet rounded-lg p-6 mb-6 text-left">
                  <h2 className="text-lg font-semibold mb-3 text-rs-green">What to do next:</h2>
                  <ul className="flex flex-col gap-2 text-rs-off-white/90">
                    <li className="flex items-start">
                      <span className="text-rs-green mr-2">•</span>
                      <span>Check if the verification link has expired (links are valid for 48 hours)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-rs-green mr-2">•</span>
                      <span>Request a new verification email using the button below</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-rs-green mr-2">•</span>
                      <span>Contact support if the problem persists</span>
                    </li>
                  </ul>
                </div>

                {resendMessage && (
                  <div className={`mb-4 p-3 rounded-lg ${resendMessage.includes('sent') ? 'bg-rs-green/20 text-rs-green' : 'bg-rs-red/20 text-rs-red'}`}>
                    {resendMessage}
                  </div>
                )}

                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => window.location.href = '/authorization'}
                    className="green !text-lg !px-8 !py-3"
                  >
                    Go to Login
                  </button>
                  <button
                    onClick={handleResendEmail}
                    disabled={resendLoading}
                    className="outline text-lg px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendLoading ? 'Sending...' : 'Resend Email'}
                  </button>
                </div>

                {showSupportLink && (<p className="mt-6 text-sm text-rs-off-white/60">
                  Need help? <a href="mailto:support@resinsmart.ai" className="text-rs-green underline">Contact Support</a>
                </p>
                  )
                }
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
